import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { createPortal } from 'react-dom'

import { DesktopDropdown } from './DesktopDropdown'
import { DesktopModal } from './DesktopModal'
import {
  useDropdownPosition,
  useFilteredOptions,
  useKeyboardNavigation,
  useOutsideClick,
  useVirtualization,
} from './hooks'
import { MobileBottomSheet } from './MobileBottomSheet'
import { useFocusAndScrollSideEffects } from './SearchableSelect-helpers'
import { TriggerButton } from './TriggerButton'
import {
  type SearchableSelectProps,
  type SelectOption,
  defaultFilterOption,
} from './types'
import { useIsMobile } from '../popover'

interface CoreState {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  highlightedIndex: number
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>
  scrollTop: number
  setScrollTop: React.Dispatch<React.SetStateAction<number>>
}

function useCoreState(): CoreState {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [scrollTop, setScrollTop] = useState(0)
  return { isOpen, setIsOpen, searchQuery, setSearchQuery, highlightedIndex, setHighlightedIndex, scrollTop, setScrollTop }
}

interface OptionsListPropsInput<T> {
  filteredOptions: SelectOption<T>[]
  value: T | null | undefined
  highlightedIndex: number
  isLoading: boolean
  noOptionsText: string
  virtualized: boolean
  itemHeight: number
  maxVisibleItems: number
  listHeight: number
  totalHeight: number
  startIndex: number
  endIndex: number
  offsetY: number
  resolvedFooterAction: React.ReactNode
  searchInputRef: React.RefObject<HTMLInputElement>
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => React.ReactNode
  onSearchChange: (q: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onSelect: (option: SelectOption<T>) => void
  onSetHighlightedIndex: React.Dispatch<React.SetStateAction<number>>
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void
}

interface PortalsProps<T> {
  isOpen: boolean
  isMobile: boolean
  modal: boolean
  dropdownPosition: ReturnType<typeof useDropdownPosition>['dropdownPosition']
  dropdownRef: React.RefObject<HTMLDivElement>
  placeholder: string
  searchPlaceholder: string
  searchQuery: string
  optionsListProps: OptionsListPropsInput<T>
  onClose: () => void
}

function SearchablePortals<T>(p: PortalsProps<T>): JSX.Element | null {
  if (!p.isOpen) { return null }
  if (!p.isMobile && !p.modal && p.dropdownPosition) {
    return createPortal(
      <DesktopDropdown
        {...p.optionsListProps}
        dropdownPosition={p.dropdownPosition}
        dropdownRef={p.dropdownRef}
        searchPlaceholder={p.searchPlaceholder}
        searchQuery={p.searchQuery}
      />,
      document.body,
    )
  }
  if (!p.isMobile && p.modal) {
    return createPortal(
      <DesktopModal
        {...p.optionsListProps}
        dropdownRef={p.dropdownRef}
        placeholder={p.placeholder}
        searchPlaceholder={p.searchPlaceholder}
        searchQuery={p.searchQuery}
        onClose={p.onClose}
      />,
      document.body,
    )
  }
  if (p.isMobile) {
    return createPortal(
      <MobileBottomSheet
        {...p.optionsListProps}
        placeholder={p.placeholder}
        searchPlaceholder={p.searchPlaceholder}
        searchQuery={p.searchQuery}
        onClose={p.onClose}
      />,
      document.body,
    )
  }
  return null
}

interface FieldMessageProps {
  hideMessage: boolean
  showError: boolean
  error?: string
  helperText?: string
}

function FieldMessage({ hideMessage, showError, error, helperText }: FieldMessageProps): JSX.Element | null {
  if (hideMessage) { return null }
  return (
    <div className="el-field-message">
      {showError && (
        <p className="text-sm text-destructive" role="alert">{error}</p>
      )}
      {!showError && helperText && (
        <p className="text-sm text-muted-foreground">{helperText}</p>
      )}
    </div>
  )
}

interface UseHandlersInput<T> {
  state: CoreState
  onChange?: (value: T | null, option: SelectOption<T> | null) => void
}

interface SearchableHandlers<T> {
  handleClose: () => void
  handleSelect: (option: SelectOption<T>) => void
  handleClear: (e: React.MouseEvent) => void
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void
}

function useSearchableHandlers<T>({ state, onChange }: UseHandlersInput<T>): SearchableHandlers<T> {
  const handleClose = useCallback((): void => { state.setIsOpen(false); state.setSearchQuery('') }, [state])
  const handleSelect = useCallback((option: SelectOption<T>) => {
    if (option.disabled) { return }
    onChange?.(option.value, option)
    state.setIsOpen(false)
    state.setSearchQuery('')
  }, [onChange, state])
  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onChange?.(null, null)
  }, [onChange])
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    state.setScrollTop(e.currentTarget.scrollTop)
  }, [state])
  return { handleClose, handleSelect, handleClear, handleScroll }
}

interface BodyProps<T> {
  state: CoreState
  containerRef: React.RefObject<HTMLDivElement>
  buttonRef: React.RefObject<HTMLButtonElement>
  dropdownRef: React.RefObject<HTMLDivElement>
  dropdownPosition: ReturnType<typeof useDropdownPosition>['dropdownPosition']
  isMobile: boolean
  isModal: boolean
  isDisabled?: boolean
  isClearable: boolean
  shouldHideMessage: boolean
  hasError: boolean
  className?: string
  id?: string
  size: 'sm' | 'md' | 'lg'
  validationState: 'default' | 'invalid' | 'valid'
  placeholder: string
  searchPlaceholder: string
  error?: string
  helperText?: string
  selectedOption: SelectOption<T> | null
  renderValue?: (option: SelectOption<T>) => React.ReactNode
  optionsListProps: OptionsListPropsInput<T>
  handlers: SearchableHandlers<T>
  handleKeyDown: (e: React.KeyboardEvent) => void
}

function SearchableSelectBody<T>(p: BodyProps<T>): JSX.Element {
  return (
    <div ref={p.containerRef} className="relative w-full">
      <TriggerButton
        buttonRef={p.buttonRef}
        className={p.className}
        clearable={p.isClearable}
        disabled={p.isDisabled}
        id={p.id}
        isOpen={p.state.isOpen}
        placeholder={p.placeholder}
        renderValue={p.renderValue}
        selectedOption={p.selectedOption}
        size={p.size}
        validationState={p.validationState}
        onClear={p.handlers.handleClear}
        onClick={() => !p.isDisabled && p.state.setIsOpen(!p.state.isOpen)}
        onKeyDown={p.handleKeyDown}
      />
      <SearchablePortals
        dropdownPosition={p.dropdownPosition}
        dropdownRef={p.dropdownRef}
        isMobile={p.isMobile}
        isOpen={p.state.isOpen}
        modal={p.isModal}
        optionsListProps={p.optionsListProps}
        placeholder={p.placeholder}
        searchPlaceholder={p.searchPlaceholder}
        searchQuery={p.state.searchQuery}
        onClose={p.handlers.handleClose}
      />
      <FieldMessage error={p.error} helperText={p.helperText} hideMessage={p.shouldHideMessage} showError={p.hasError} />
    </div>
  )
}

interface ComputedSettings<T> {
  state: CoreState
  dropdownRef: React.RefObject<HTMLDivElement>
  searchInputRef: React.RefObject<HTMLInputElement>
  buttonRef: React.RefObject<HTMLButtonElement>
  isMobile: boolean
  filteredOptions: SelectOption<T>[]
  totalHeight: number
  listHeight: number
  startIndex: number
  endIndex: number
  offsetY: number
  containerRef: React.RefObject<HTMLDivElement>
  dropdownPosition: ReturnType<typeof useDropdownPosition>['dropdownPosition']
  handlers: SearchableHandlers<T>
  selectedOption: SelectOption<T> | null
  resolvedFooterAction: React.ReactNode
  handleKeyDown: (e: React.KeyboardEvent) => void
}

function useSearchableSettings<T>(props: SearchableSelectProps<T>, ref: React.ForwardedRef<HTMLButtonElement>): ComputedSettings<T> {
  const {
    options, value, onChange, validationState: _vs, error: _err,
    filterOption = defaultFilterOption, virtualized: isVirtualized = false,
    itemHeight = 38, maxVisibleItems = 8, modal: isModal = false,
    onOpenChange, footerAction,
  } = props
  const state = useCoreState()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement, [])

  const isMobile = useIsMobile()
  const filteredOptions = useFilteredOptions(options, state.searchQuery, filterOption)
  const { totalHeight, listHeight, startIndex, endIndex, offsetY } = useVirtualization({
    filteredOptionsCount: filteredOptions.length,
    itemHeight, maxVisibleItems, virtualized: isVirtualized, scrollTop: state.scrollTop,
  })
  const { containerRef, dropdownPosition } = useDropdownPosition({
    isOpen: state.isOpen, isMobile, modal: isModal,
    filteredOptionsCount: filteredOptions.length, itemHeight, listHeight,
  })

  const handlers = useSearchableHandlers<T>({ state, onChange })
  useOutsideClick(containerRef, dropdownRef, { isOpen: state.isOpen, isMobile, onClose: handlers.handleClose })
  useFocusAndScrollSideEffects({ isOpen: state.isOpen, searchInputRef, buttonRef, isMobile, onOpenChange })
  useEffect(() => {
    state.setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1)
  }, [filteredOptions.length, state])

  const selectedOption = options.find((opt) => opt.value === value) ?? null
  const closeDropdown = useCallback(() => state.setIsOpen(false), [state])
  const resolvedFooterAction = typeof footerAction === 'function' ? footerAction(closeDropdown) : footerAction

  const handleKeyDown = useKeyboardNavigation({
    isOpen: state.isOpen, filteredOptions, highlightedIndex: state.highlightedIndex,
    onOpen: () => state.setIsOpen(true), onClose: () => state.setIsOpen(false),
    onSetHighlightedIndex: state.setHighlightedIndex, onSelect: handlers.handleSelect,
    onSetSearchQuery: state.setSearchQuery,
  })

  return {
    state, dropdownRef, searchInputRef, buttonRef, isMobile, filteredOptions,
    totalHeight, listHeight, startIndex, endIndex, offsetY,
    containerRef, dropdownPosition, handlers, selectedOption,
    resolvedFooterAction, handleKeyDown,
  }
}

interface BuildOptionsListInput<T> {
  s: ComputedSettings<T>
  value: T | null | undefined
  isLoading: boolean
  noOptionsText: string
  isVirtualized: boolean
  itemHeight: number
  maxVisibleItems: number
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => React.ReactNode
}

function buildOptionsListProps<T>(input: BuildOptionsListInput<T>): OptionsListPropsInput<T> {
  const { s, value, isLoading, noOptionsText, isVirtualized, itemHeight, maxVisibleItems, renderOption } = input
  return {
    filteredOptions: s.filteredOptions, value, highlightedIndex: s.state.highlightedIndex, isLoading,
    noOptionsText, virtualized: isVirtualized, itemHeight, maxVisibleItems, listHeight: s.listHeight,
    totalHeight: s.totalHeight, startIndex: s.startIndex, endIndex: s.endIndex, offsetY: s.offsetY,
    resolvedFooterAction: s.resolvedFooterAction, searchInputRef: s.searchInputRef,
    renderOption, onSearchChange: s.state.setSearchQuery, onKeyDown: s.handleKeyDown,
    onSelect: s.handlers.handleSelect, onSetHighlightedIndex: s.state.setHighlightedIndex, onScroll: s.handlers.handleScroll,
  }
}

function SearchableSelectInner<T = string>(
  props: SearchableSelectProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>,
): JSX.Element {
  const {
    value, placeholder = 'Select...', searchPlaceholder = 'Search...',
    validationState: validationStateProp, error, helperText, size = 'md',
    hideMessage: shouldHideMessage = false, clearable: isClearable = true,
    renderOption, renderValue, noOptionsText = 'No options found',
    isLoading = false, virtualized: isVirtualized = false, itemHeight = 38,
    maxVisibleItems = 8, disabled: isDisabled, id, className, modal: isModal = false,
  } = props

  const s = useSearchableSettings<T>(props, ref)
  const validationState = error ? 'invalid' : (validationStateProp ?? 'default')
  const hasError = !!error && validationState === 'invalid'
  const optionsListProps = buildOptionsListProps({
    s, value, isLoading, noOptionsText, isVirtualized, itemHeight, maxVisibleItems, renderOption,
  })

  return (
    <SearchableSelectBody
      buttonRef={s.buttonRef}
      className={className}
      containerRef={s.containerRef}
      dropdownPosition={s.dropdownPosition}
      dropdownRef={s.dropdownRef}
      error={error}
      handleKeyDown={s.handleKeyDown}
      handlers={s.handlers}
      hasError={hasError}
      helperText={helperText}
      id={id}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMobile={s.isMobile}
      isModal={isModal}
      optionsListProps={optionsListProps}
      placeholder={placeholder}
      renderValue={renderValue}
      searchPlaceholder={searchPlaceholder}
      selectedOption={s.selectedOption}
      shouldHideMessage={shouldHideMessage}
      size={size}
      state={s.state}
      validationState={validationState}
    />
  )
}

export const SearchableSelect = forwardRef(SearchableSelectInner) as <T = string>(
  props: SearchableSelectProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> }
) => ReturnType<typeof SearchableSelectInner>

export default SearchableSelect
